// API Service for communicating with the backend
const getApiBaseUrl = () => {
  // If explicitly set, use that
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  
  // For IIS deployment and development, use relative paths
  // This works because the Express server serves both static files and API
  return '/api';
};

const API_BASE_URL = getApiBaseUrl();

interface ApiResponse<T = any> {
  success: boolean;
  message?: string;
  data?: T;
  error?: string;
}

interface CreateDonationRequest {
  amount: number;
  donationType: 'one-time' | 'monthly';
  firstName: string;
  lastName: string;
  email: string;
  cellNumber: string;
  campaign?: string;
}

interface DonationResponse {
  donationId: string;
  amount: number;
  donationType: string;
  donationStatus: string;
  createdAt: string;
}

interface PayFastData {
  merchant_id: string;
  merchant_key: string;
  return_url: string;
  cancel_url: string;
  notify_url: string;
  amount: number;
  item_name: string;
  item_description?: string;
  m_payment_id?: string;
  custom_int1?: number;
  custom_str1?: string;
}

class ApiService {
  private static instance: ApiService;

  private constructor() {}

  public static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService();
    }
    return ApiService.instance;
  }

  /**
   * Make HTTP request with error handling
   */
  private async makeRequest<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> {
    try {
      const url = `${API_BASE_URL}${endpoint}`;
      
      const defaultOptions: RequestInit = {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers
        },
        ...options
      };

      console.log(`API Request: ${options.method || 'GET'} ${url}`);
      
      const response = await fetch(url, defaultOptions);
      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || `HTTP error! status: ${response.status}`);
      }

      console.log(`API Response: ${response.status}`, data);
      return data;

    } catch (error) {
      console.error('API Request failed:', error);
      
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return {
          success: false,
          message: 'Unable to connect to server. Please check if the API server is running.',
          error: error.message
        };
      }

      return {
        success: false,
        message: error instanceof Error ? error.message : 'Unknown error occurred',
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Create a new donation
   */
  public async createDonation(donationData: CreateDonationRequest): Promise<ApiResponse<DonationResponse>> {
    return this.makeRequest<DonationResponse>('/donations', {
      method: 'POST',
      body: JSON.stringify(donationData)
    });
  }

  /**
   * Update donation with PayFast data
   */
  public async updateWithPayFastData(donationId: string, payfastData: PayFastData): Promise<ApiResponse> {
    return this.makeRequest(`/donations/${donationId}/payfast`, {
      method: 'PUT',
      body: JSON.stringify(payfastData)
    });
  }

  /**
   * Update payment status (for PayFast webhooks)
   */
  public async updatePaymentStatus(donationId: string, paymentData: any): Promise<ApiResponse> {
    return this.makeRequest(`/donations/${donationId}/payment-status`, {
      method: 'PUT',
      body: JSON.stringify(paymentData)
    });
  }

  /**
   * Get donation by ID
   */
  public async getDonationById(donationId: string): Promise<ApiResponse> {
    return this.makeRequest(`/donations/${donationId}`);
  }

  /**
   * Get donations by email
   */
  public async getDonationsByEmail(email: string): Promise<ApiResponse> {
    return this.makeRequest(`/donations/email/${encodeURIComponent(email)}`);
  }

  /**
   * Get donation statistics
   */
  public async getDonationStats(): Promise<ApiResponse> {
    return this.makeRequest('/donations/stats');
  }

  /**
   * Health check
   */
  public async healthCheck(): Promise<ApiResponse> {
    return this.makeRequest('/donations/health');
  }

  /**
   * Server health check
   */
  public async serverHealthCheck(): Promise<ApiResponse> {
    return this.makeRequest('/health', { method: 'GET' });
  }
}

// Export singleton instance
export const apiService = ApiService.getInstance();
export default ApiService;

// Export types for use in components
export type { 
  CreateDonationRequest, 
  DonationResponse, 
  PayFastData, 
  ApiResponse 
};
